<template>
    <nav
        class="flex max-h-full flex-col gap-y-6 overflow-y-auto bg-white pt-7 text-base"
        style="width: 250px"
    >
        <div class="relative flex items-center gap-x-2 pl-4">
            <img
                v-if="userStore.getTenantLogo"
                :src="userStore.getTenantLogo"
                alt="Company logo"
                class="h-7 max-w-44 shrink-0 object-contain object-left"
            />
            <SvgIcon
                v-else
                class="h-7 w-32 shrink-0"
                name="movepro_logo"
            />
        </div>

        <div class="grid">
            <template
                v-for="section in sections"
                :key="section.key"
            >
                <template v-if="section.key === 'my_work'">
                    <hr class="border-muval-gray-8 mx-4 my-2" />
                    <MyWorkNavHeader
                        v-model:open="myWorkOpen"
                        :count="section.count"
                    />
                </template>
                <div
                    v-show="section.key !== 'my_work' || myWorkOpen"
                    class="grid"
                    :data-test="`nav-section-${section.key}`"
                >
                    <template
                        v-for="(item, idx) in section.items"
                        :key="idx"
                    >
                        <component :is="item?.subitems ? Disclosure : 'div'">
                            <router-link
                                v-if="!item.subitems"
                                v-slot="{ isActive }"
                                :to="{ path: item.route }"
                                @click.prevent="canRefresh(item.route) && $router.go()"
                                @click.ctrl="modClick(item.route)"
                                @click.meta="modClick(item.route)"
                            >
                                <div
                                    :key="item"
                                    class="hover:bg-muval-gray-6 flex h-10 items-center gap-4 overflow-x-clip px-4 text-base font-normal no-underline"
                                >
                                    <SvgIcon
                                        :class="[
                                            { 'text-brand!': subIsActive(item.route) },
                                            { 'text-muval-gray-1 dark:text-white': !isActive },
                                        ]"
                                        class="h-6 w-6 shrink-0 transition"
                                        :name="item.icon"
                                    />
                                    <span
                                        class="opacity-100 transition duration-200 ease-in-out"
                                        :class="[subIsActive(item.route) ? 'text-brand' : 'text-muval-gray-1']"
                                    >
                                        {{ item.name }}
                                    </span>
                                    <div
                                        v-if="item.meta?.count"
                                        class="bg-brand flex size-[18px] items-center justify-center rounded-full text-xs font-bold text-white"
                                        style="margin-top: -1px"
                                    >
                                        {{ item.meta?.count }}
                                    </div>
                                </div>
                            </router-link>
                            <template v-else>
                                <DisclosureButton
                                    v-slot="{ open }"
                                    class="w-full"
                                >
                                    <div
                                        :key="item"
                                        class="hover:bg-muval-gray-6 flex h-10 items-center gap-4 px-4 text-base font-normal no-underline"
                                    >
                                        <SvgIcon
                                            :class="[
                                                {
                                                    'text-brand':
                                                        open &&
                                                        item.subitems.some((subitem) => subIsActive(subitem.route)),
                                                },
                                                { 'text-muval-gray-1 dark:text-white': !isActive },
                                            ]"
                                            class="h-6 w-6 shrink-0 transition"
                                            :name="item.icon"
                                        />
                                        <span
                                            class="text-muval-gray-1 opacity-100 transition duration-200 ease-in-out dark:text-gray-300"
                                            :class="[
                                                {
                                                    'text-brand dark:text-brand':
                                                        open &&
                                                        item.subitems.some((subitem) => subIsActive(subitem.route)),
                                                },
                                                { 'text-gray-800:': isActive },
                                            ]"
                                        >
                                            {{ item.name }}
                                        </span>
                                        <div
                                            v-if="item.meta?.count && !open"
                                            class="bg-brand flex size-[18px] items-center justify-center rounded-full text-xs font-bold text-white"
                                        >
                                            {{ item.meta?.count }}
                                        </div>
                                    </div>
                                </DisclosureButton>

                                <DisclosurePanel
                                    class="flex flex-col gap-1"
                                    as="div"
                                >
                                    <router-link
                                        v-for="(subitem, subItemIndex) in item.subitems"
                                        :key="subItemIndex"
                                        v-slot="{ isActive }"
                                        :to="{ path: subitem.route }"
                                        @click.prevent="canRefresh(subitem.route) && $router.go()"
                                        @click.ctrl="modClick(subitem.route)"
                                        @click.meta="modClick(subitem.route)"
                                    >
                                        <div
                                            v-if="subitem && subitem.name != 'Scheduler'"
                                            :key="subitem"
                                            class="hover:bg-muval-gray-6 flex h-9 items-center gap-4 px-4 text-base font-normal no-underline"
                                        >
                                            <div class="flex h-6 w-6 shrink-0 items-center justify-center">
                                                <div
                                                    class="bg-muval-gray-4 h-1.5 w-1.5 rounded-full"
                                                    :class="[subitem.route === $route.path && 'bg-muval-brand!']"
                                                ></div>
                                            </div>
                                            <span
                                                class="text-muval-gray-1 text-sm opacity-100 transition duration-200 ease-in-out dark:text-gray-300"
                                                :class="[
                                                    {
                                                        'text-brand!': subitem.route === $route.path,
                                                    },
                                                    { 'text-gray-800:': isActive },
                                                ]"
                                            >
                                                {{ subitem.name }}
                                            </span>
                                            <div
                                                v-if="subitem.meta?.count"
                                                class="bg-brand z-50 flex size-[18px] items-center justify-center rounded-full text-xs font-bold text-white"
                                            >
                                                {{ subitem.meta?.count }}
                                            </div>
                                        </div>
                                    </router-link>
                                </DisclosurePanel>
                            </template>
                        </component>
                    </template>
                </div>
            </template>
        </div>
    </nav>
</template>
<script setup>
import { Disclosure, DisclosureButton, DisclosurePanel } from '@headlessui/vue';
import { useJobCounts } from '@composables/useJobCounts';
import { usePendingTasksCount } from '@composables/useTodaysTasks';

const userStore = useUserStore();

const route = useRoute();
const router = useRouter();

const { newLeadsCount, pendingJobsCount, conflictsCount } = useJobCounts();
const { count: pendingTasksCount } = usePendingTasksCount();

// Update navigation items with counts
const navigationWithCounts = computed(() => {
    if (!userStore.navigation) return [];

    // Check if user should see count badges
    const shouldShowCounts = userStore.shouldShowCountBadges();

    return userStore.navigation.map((item) => {
        const updatedItem = { ...item };

        // Add count to Leads navigation (only if user should see counts)
        if ((item.route === '/orders' || item.name === 'Leads') && shouldShowCounts) {
            updatedItem.meta = {
                ...updatedItem.meta,
                count: newLeadsCount.value > 0 ? newLeadsCount.value : null,
            };
        }

        // Add count to Jobs navigation (only if user should see counts and route is /jobs)
        if (item.route === '/jobs' && shouldShowCounts) {
            updatedItem.meta = {
                ...updatedItem.meta,
                count: pendingJobsCount.value > 0 ? pendingJobsCount.value : null,
            };
        }

        // Crew badge: tasks awaiting acceptance. Not gated by shouldShowCountBadges —
        // that helper deliberately excludes FIELD_STAFF/CONTRACTOR (it's for leads/jobs).
        if (item.route === '/field-staff/notifications') {
            updatedItem.meta = {
                ...updatedItem.meta,
                count: pendingTasksCount.value > 0 ? pendingTasksCount.value : null,
            };
        }

        // Handle subitems if they exist
        if (item.subitems) {
            // Ensure subitems is always an array
            const subitemsArray = Array.isArray(item.subitems) ? item.subitems : Object.values(item.subitems);
            updatedItem.subitems = subitemsArray.map((subitem) => {
                const updatedSubitem = { ...subitem };

                if ((subitem.route === '/orders' || subitem.name === 'Leads') && shouldShowCounts) {
                    updatedSubitem.meta = {
                        ...updatedSubitem.meta,
                        count: newLeadsCount.value > 0 ? newLeadsCount.value : null,
                    };
                }

                if (subitem.route === '/jobs' && shouldShowCounts) {
                    updatedSubitem.meta = {
                        ...updatedSubitem.meta,
                        count: pendingJobsCount.value > 0 ? pendingJobsCount.value : null,
                    };
                }

                if (subitem.route === '/scheduler/fix-list') {
                    updatedSubitem.meta = {
                        ...updatedSubitem.meta,
                        count: conflictsCount.value > 0 ? conflictsCount.value : null,
                    };
                }

                if (subitem.route === '/field-staff/notifications') {
                    updatedSubitem.meta = {
                        ...updatedSubitem.meta,
                        count: pendingTasksCount.value > 0 ? pendingTasksCount.value : null,
                    };
                }

                return updatedSubitem;
            });

            // Sum subitem counts onto the parent (shown when disclosure is closed)
            // Exclude fix list count — it should only appear when the menu is expanded
            const subitemTotal = updatedItem.subitems
                .filter((sub) => sub.route !== '/scheduler/fix-list')
                .reduce((sum, sub) => sum + (sub.meta?.count ?? 0), 0);
            if (subitemTotal > 0) {
                updatedItem.meta = { ...updatedItem.meta, count: subitemTotal };
            }
        }

        return updatedItem;
    });
});

// A mixed-role user's crew pages follow the office items under a collapsible "My work" header
const myWorkOpen = useStorage('my-work-open', true);

const sections = computed(() => {
    const myWork = navigationWithCounts.value.filter((item) => item.section === 'my_work');
    const office = navigationWithCounts.value.filter((item) => item.section !== 'my_work');

    return myWork.length ?
            [
                { key: 'office', items: office },
                {
                    key: 'my_work',
                    items: myWork,
                    count: myWork.reduce((sum, item) => sum + (item.meta?.count ?? 0), 0),
                },
            ]
        :   [{ key: 'office', items: office }];
});

function subIsActive(input) {
    const paths = Array.isArray(input) ? input : [input];
    if (route.name == 'Order' && router?.options?.history?.state?.back == '/jobs' && input == '/jobs') {
        return true;
    } else if (route.name == 'Order' && router?.options?.history?.state?.back == '/jobs' && input == '/orders') {
        return false;
    } else {
        return paths.some((path) => {
            return route.path.indexOf(path) === 0; // current path starts with this path string
        });
    }
}

function modClick(route) {
    window.open(route, '_blank');
}

function canRefresh(input) {
    return route.path === input;
}
</script>
